import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Mail } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { queryKeys } from "@/lib/query-keys";
import { Button } from "@/components/ui/button";

const PARTNERSHIPS_EMAIL = "partnerships@bookboost.io";
// How often to re-check whether an admin has linked this account to a partner.
const REFRESH_INTERVAL_MS = 15_000;

// Shown when a signed-in user has the partner role but no partner linkage yet
// (ProtectedRoute redirects here when session.partnerId is null). It polls the
// session in the background, so the moment an admin links the account the
// refreshed session flips partnerId and ProtectedRoute forwards to the
// dashboard — no manual reload needed.
export function OnboardingPendingPage() {
  const queryClient = useQueryClient();
  const { session } = useAuth();

  useEffect(() => {
    const interval = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.me() });
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [queryClient]);

  const mailto = `mailto:${PARTNERSHIPS_EMAIL}?subject=${encodeURIComponent(
    "Colab partner access — pending assignment",
  )}${
    session?.email
      ? `&body=${encodeURIComponent(
          `Hi Bookboost team,\n\nMy Colab account (${session.email}) is signed in but not yet linked to a partner. Could you complete the assignment?\n\nThanks!`,
        )}`
      : ""
  }`;

  return (
    <div className="mx-auto max-w-xl rounded-xl border bg-card p-8 shadow-sm">
      <h1 className="font-display text-2xl font-semibold tracking-tight">
        Almost there
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Your account is set up, but it isn&apos;t linked to a partner profile
        yet. A Bookboost partnerships admin completes this step — once they do,
        this page moves you straight into your portal automatically.
      </p>

      <ol className="mt-6 space-y-3 text-sm">
        <li className="flex gap-3">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary">
            1
          </span>
          <span className="text-muted-foreground">
            <span className="font-medium text-foreground">You&apos;ve signed in.</span>{" "}
            Your login is ready.
          </span>
        </li>
        <li className="flex gap-3">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary">
            2
          </span>
          <span className="text-muted-foreground">
            <span className="font-medium text-foreground">
              We link you to your partner.
            </span>{" "}
            This usually happens within a few minutes during onboarding.
          </span>
        </li>
        <li className="flex gap-3">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary">
            3
          </span>
          <span className="text-muted-foreground">
            <span className="font-medium text-foreground">
              Your portal opens.
            </span>{" "}
            You&apos;ll be redirected here automatically — no need to refresh.
          </span>
        </li>
      </ol>

      <div className="mt-6 flex items-center gap-3">
        <Button asChild>
          <a href={mailto}>
            <Mail className="mr-2 h-4 w-4" />
            Contact partnerships
          </a>
        </Button>
        <p className="text-xs text-muted-foreground">
          Taking longer than expected? Reach out and we&apos;ll sort it out.
        </p>
      </div>
    </div>
  );
}
